"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useEffect, useState, useRef } from "react";

interface TripNavigationProps {
    navigation: {
        label: string;
        id: string;
        active: string;
    }[];
}

const TripNavigation = ({ navigation }: TripNavigationProps) => {
    const tripPrev = `tripNavigationPrev`;
    const tripNext = `tripNavigationNext`;

    const [activeSection, setActiveSection] = useState<string>("about-trek-active");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const swiperRef = useRef<any>(null); // Reference for the Swiper instance
    const [translateX, setTranslateX] = useState(0)

    useEffect(() => {
        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const newActiveSection = entry.target.id;
                    setActiveSection(newActiveSection);

                    // Find the index of the intersecting section and set it as activeIndex
                    const newIndex = navigation.findIndex(
                        (navItem) => navItem.active.replace("#", "") === newActiveSection
                    );

                    if (newIndex > 4) {

                        // Automatically slide to the corresponding index
                        if (swiperRef.current) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                            swiperRef.current.slideTo(Math.floor(newIndex - 1)); // Swiping to the slide containing the intersecting section
                        }
                    } else if (newIndex !== -1) {

                        // Automatically slide to the corresponding index
                        if (swiperRef.current) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                            swiperRef.current.slideTo(Math.floor(newIndex / 4)); // Swiping to the slide containing the intersecting section
                        }
                    }

                    if (newIndex > 0 && newIndex < navigation.length) {
                        // Adjust the translation by 9rem (144px) for each intersecting section
                        setTranslateX(-(newIndex - 1) * 9 * 16); // 9rem in pixels (1rem = 16px)
                    }
                }
            });
        };

        const options = {
            root: null,
            rootMargin: "0px 0px -9% 0px",
            threshold: 0.4, // 40% of the section should be visible
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        navigation.forEach((navItem) => {
            const sectionId = navItem.active.replace("#", "");
            const section = document.getElementById(sectionId);
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            navigation.forEach((navItem) => {
                const sectionId = navItem.active.replace("#", "");
                const section = document.getElementById(sectionId);
                if (section) {
                    observer.unobserve(section);
                }
            });
        };
    }, [navigation]);

    return (
        <>
            <div className='flex flex-nowrap md:hidden overflow-x-scroll hide-vertical-scrollbar'>
                <div
                    className={`flex transition-transform duration-300 transform`}
                    style={{ transform: `translateX(${translateX}px)` }}
                >
                    {navigation.map((data) => (
                        <Link
                            key={data.id}
                            href={data.id}
                            className={cn(
                                'flex justify-center items-center text-white/60 text-center h-full w-36 py-2 text-nowrap shrink-0',
                                activeSection === data.active.replace("#", "") &&
                                "bg-custom-primary-dark text-white border-b"
                            )}
                        >
                            <span className="relative w-fit">
                                {data.label}
                                <span
                                    className={cn(
                                        "h-0.5 w-full bg-yellow-400 scale-y-75 absolute top-full left-0",
                                        activeSection === data.active.replace("#", "")
                                            ? "scale-x-100"
                                            : "scale-x-0"
                                    )}
                                />
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
            <div className="hidden md:block h-full w-full">
                <Swiper
                    slidesPerView={4}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    navigation={{
                        prevEl: `.${tripPrev}`,
                        nextEl: `.${tripNext}`,
                    }}
                    modules={[Navigation]}
                    className="mySwiper"
                >
                    {navigation.map((data) => (
                        <SwiperSlide key={data.id} className="border-r border-r-white">
                            <Link
                                href={data.id}
                                className={cn(
                                    "flex justify-center items-center text-white/60 text-center h-full w-full py-2 text-nowrap",
                                    activeSection === data.active.replace("#", "") &&
                                    "bg-custom-primary-dark text-white"
                                )}
                            >
                                <span className="relative w-fit">
                                    {data.label}
                                    <span
                                        className={cn(
                                            "h-0.5 w-full bg-yellow-400 scale-y-75 absolute top-full left-0",
                                            activeSection === data.active.replace("#", "")
                                                ? "scale-x-100"
                                                : "scale-x-0"
                                        )}
                                    />
                                </span>
                            </Link>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <button
                    className={cn(
                        "absolute z-10 top-1/2 left-0 -translate-y-1/2 h-full w-fit p-0.5 lg:p-1 bg-custom-primary-dark text-white disabled:cursor-not-allowed disabled:text-white/60 rounded-bl-lg rounded-tl-lg",
                        tripPrev
                    )}
                >
                    <ChevronLeft className="h-full w-full" />
                </button>
                <button
                    className={cn(
                        "absolute z-10 top-1/2 right-0 -translate-y-1/2 h-full w-fit p-0.5 lg:p-1 bg-custom-primary-dark text-white disabled:cursor-not-allowed disabled:text-white/60 rounded-br-lg rounded-tr-lg",
                        tripNext
                    )}
                >
                    <ChevronRight className="h-full w-full" />
                </button>
            </div>
        </>
    );
};

export default TripNavigation;